<script setup lang="ts">
import { UploadInput } from '@metapic-apps/shared'
import type { UploadRawFile } from 'element-plus'
import { ref, watch } from 'vue'

import { useUploadTempImageRequest } from '@/api/image.api'
import RefreshIcon from '@/assets/icons/refresh.svg?component'
import { defaultStyle } from '@/components/ReturnAdForm/ReturnAdForm.helpers'
import type { ProcessableImageType } from '@/lib/types/common.types'
import type { ReturnAdFormType } from '@/lib/types/return-ad.types'
import {
  BoxComponent,
  ButtonComponent,
  ColorPickerComponent,
  FlexComponent,
  InputComponent,
  RadioButton,
  RadioButtonGroupComponent,
  TitleComponent,
  TypographyComponent,
} from '@/ui-library'

defineProps<{ image?: ProcessableImageType }>()

const text = defineModel<ReturnAdFormType['styles']['text']>('text')
const backgroundType = defineModel<ReturnAdFormType['styles']['backgroundType']>('backgroundType')
const backgroundColor = defineModel<ReturnAdFormType['styles']['backgroundColor']>('backgroundColor')
const textColor = defineModel<ReturnAdFormType['styles']['textColor']>('textColor')
const textPosition = defineModel<ReturnAdFormType['styles']['textPosition']>('textPosition')
const buttonBackgroundColor = defineModel<ReturnAdFormType['styles']['buttonBackgroundColor']>('buttonBackgroundColor')
const buttonTextColor = defineModel<ReturnAdFormType['styles']['buttonTextColor']>('buttonTextColor')
const tempImage = defineModel<ReturnAdFormType['tempImage']>('tempImage')
const isImageUploading = defineModel<boolean>('isImageUploading')

const rawImage = ref<UploadRawFile | Blob | undefined>()
const imageToUpload = ref<UploadRawFile | Blob | undefined>()

const { uploadImage, image: uploadedImage, isLoading: isImageLoading } = useUploadTempImageRequest()

const resetToDefault = () => {
  tempImage.value = undefined
  backgroundType.value = defaultStyle.backgroundType
  backgroundColor.value = defaultStyle.backgroundColor
  textColor.value = defaultStyle.textColor
  buttonBackgroundColor.value = defaultStyle.buttonBackgroundColor
  buttonTextColor.value = defaultStyle.buttonTextColor
  textPosition.value = defaultStyle.textPosition
}

watch(
  () => imageToUpload.value,
  async () => {
    if (imageToUpload.value) {
      await uploadImage(imageToUpload.value as UploadRawFile)
    }
  },
)

watch(
  () => isImageLoading.value,
  () => {
    isImageUploading.value = isImageLoading.value
  },
)

watch(
  () => uploadedImage.value,
  () => {
    tempImage.value = uploadedImage.value ?? undefined
  },
)
</script>

<template>
  <FlexComponent direction="column" :gap="24">
    <el-form-item prop="styles.text">
      <InputComponent v-model="text" name="styles.text">
        <template #header>
          <BoxComponent :mb="8">
            <TitleComponent title="Text" />
          </BoxComponent>
        </template>
      </InputComponent>
    </el-form-item>
    <FlexComponent :gap="16" direction="column">
      <TitleComponent title="Background" variant="headline-small" />
      <RadioButtonGroupComponent v-model="backgroundType" children-width="0%" name="backgroundType">
        <RadioButton label="Solid" value="solid" />
        <RadioButton label="Image" value="image" />
      </RadioButtonGroupComponent>
      <el-form-item v-if="backgroundType === 'solid'" prop="styles.backgroundColor">
        <ColorPickerComponent v-model="backgroundColor" name="styles.backgroundColor">
          <template #header>
            <BoxComponent :mb="8">
              <TitleComponent title="Color" />
            </BoxComponent>
          </template>
        </ColorPickerComponent>
      </el-form-item>
      <div v-else>
        <BoxComponent :mb="24">
          <UploadInput
            v-model:image-thumbnail="rawImage"
            :save-on-close="true"
            prop="image"
            name="image"
            placeholder=""
            :aspect-ratio="9 / 16"
            :is-portrait-icon="true"
            :auto-upload="false"
            :image-url="image?.url"
            :is-disabled="isImageLoading"
            @crop="(rawImage) => (imageToUpload = rawImage)"
          >
            <template #label>
              <BoxComponent :mb="8">
                <TitleComponent title="Image" />
              </BoxComponent>
            </template>
            <template #placeholder>
              <BoxComponent :mb="8">
                <FlexComponent direction="column" align="center">
                  <TypographyComponent variant="body-small" color="var(--secondary-64)">
                    Must be JPG or PNG. Best 9:16 ratio.
                  </TypographyComponent>
                </FlexComponent>
              </BoxComponent>
            </template>
          </UploadInput>
        </BoxComponent>
      </div>
    </FlexComponent>

    <FlexComponent :gap="16" direction="column">
      <TitleComponent title="Text" variant="headline-small" />
      <el-form-item prop="styles.textColor">
        <ColorPickerComponent v-model="textColor" name="styles.textColor">
          <template #header>
            <BoxComponent :mb="8">
              <TitleComponent title="Color" />
            </BoxComponent>
          </template>
        </ColorPickerComponent>
      </el-form-item>

      <template v-if="backgroundType === 'image'">
        <RadioButtonGroupComponent v-model="textPosition" children-width="0%" name="textPosition">
          <template #header>
            <BoxComponent :mb="8">
              <TitleComponent title="Position" />
            </BoxComponent>
          </template>
          <RadioButton label="Center" value="center" />
          <RadioButton label="Top" value="top" />
          <RadioButton label="Bottom" value="bottom" />
        </RadioButtonGroupComponent>
      </template>
    </FlexComponent>

    <FlexComponent :gap="16" direction="column">
      <TitleComponent title="Button" variant="headline-small" />
      <FlexComponent :gap="16">
        <FlexComponent :is-child="true" flex="1">
          <el-form-item prop="styles.buttonBackgroundColor">
            <ColorPickerComponent v-model="buttonBackgroundColor" name="styles.buttonBackgroundColor">
              <template #header>
                <BoxComponent :mb="8">
                  <TitleComponent title="Color" />
                </BoxComponent>
              </template>
            </ColorPickerComponent>
          </el-form-item>
        </FlexComponent>
        <FlexComponent :is-child="true" flex="1">
          <el-form-item prop="styles.buttonTextColor">
            <ColorPickerComponent v-model="buttonTextColor" name="styles.buttonTextColor">
              <template #header>
                <BoxComponent :mb="8">
                  <TitleComponent title="Text color" />
                </BoxComponent>
              </template>
            </ColorPickerComponent>
          </el-form-item>
        </FlexComponent>
      </FlexComponent>
      <ButtonComponent variant="text" class="reset-button" @click="resetToDefault">
        <template #iconLeft>
          <RefreshIcon />
        </template>
        Reset to default
      </ButtonComponent>
    </FlexComponent>
  </FlexComponent>
</template>

<style scoped lang="scss">
.reset-button {
  padding-left: 0;
  justify-content: start;
}
</style>
